import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client/react';
import { selectEditableNameAnnotation } from '../../../redux/rendererReducer';
import { useCustomSelector } from '../../../redux/store';
import { useProjectUpdateEntityNameMutation } from '../../../types/graphqlTypes';
import { renameGroupInCache } from '../../potree/useGroupOperations';
import { getAnnotationNameProperty } from '@pointorama/potree-shared';

export const useRenameSubmit = () => {
  const [updateEntityName] = useProjectUpdateEntityNameMutation();
  const { editableNameAnnotation } = useCustomSelector((state) => state.rendererProvider, ['editableNameAnnotation']);
  const dispatch = useDispatch();
  const { projectId = '' } = useParams();
  const apolloClient = useApolloClient();

  const submitRename = useCallback(() => {
    if (editableNameAnnotation) {
      const identifier = editableNameAnnotation.id;

      if (editableNameAnnotation.type === 'annotation') {
        // Annotations: write to reactive property (auto-saves + broadcasts via Pusher)
        try {
          getAnnotationNameProperty(identifier).value = editableNameAnnotation.name;
        } catch {
          // Property service not initialized yet
        }
      } else {
        // Groups: optimistic cache write so the panel updates instantly
        // (the mutation's full-state response reconciles).
        if (editableNameAnnotation.type === 'group') {
          renameGroupInCache(apolloClient, projectId, identifier, editableNameAnnotation.name);
        }
        // Non-annotations: use direct mutation to rename groups, layers, point clouds
        updateEntityName({
          variables: {
            projectId,
            name: editableNameAnnotation.name,
            groupIdentifier: editableNameAnnotation.type === 'group' ? identifier : undefined,
            pointCloudIdentifier: editableNameAnnotation.type === 'pointcloud' ? identifier : undefined,
            cadLayerIdentifier: editableNameAnnotation.type === 'cadLayer' ? identifier : undefined,
            wmsLayerIdentifier: editableNameAnnotation.type === 'wmsLayer' ? identifier : undefined,
            orthophotoLayerIdentifier: editableNameAnnotation.type === 'orthophotoLayer' ? identifier : undefined,
          },
        });
      }

      dispatch(selectEditableNameAnnotation(undefined));
    }
  }, [dispatch, editableNameAnnotation, updateEntityName, projectId, apolloClient]);

  return submitRename;
};
